import type {
  SessionResponse,
  LoginResponse,
  LoginCredentials,
  AccountSelectResponse,
  PasswordResetRequest,
} from '../types/auth'
import { fetchOptions, fetchWithCSRFRetry, updateCSRFToken } from './csrf'

export const authApi = {
  // Get current session info
  async getSession(): Promise<SessionResponse> {
    const response = await fetch('/api/v1/session', fetchOptions())
    if (!response.ok) {
      return { authenticated: false }
    }
    return response.json()
  },

  // Login with email and password
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await fetchWithCSRFRetry('/users/sign_in', {
      ...fetchOptions('POST'),
      body: JSON.stringify({
        user: {
          email: credentials.email,
          password: credentials.password,
          remember_me: credentials.remember_me ? '1' : '0',
        },
      }),
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      return {
        success: false,
        error: data.error || 'Invalid email or password',
      }
    }

    const data = await response.json()
    if (data.csrf_token) {
      updateCSRFToken(data.csrf_token)
    }
    return data
  },

  // Logout
  async logout(): Promise<{ success: boolean }> {
    const response = await fetchWithCSRFRetry('/users/sign_out', fetchOptions('DELETE'))
    if (response.ok) {
      const data = await response.json().catch(() => ({}))
      if (data.csrf_token) {
        updateCSRFToken(data.csrf_token)
      }
    }
    return { success: response.ok }
  },

  // Select account for multi-account users
  async selectAccount(accountId: string): Promise<AccountSelectResponse> {
    const response = await fetch('/api/v1/accounts/select', {
      ...fetchOptions('PATCH'),
      body: JSON.stringify({ account_id: accountId }),
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      return { success: false, error: data.error || 'Failed to select account' }
    }

    return response.json()
  },

  // Request password reset
  async requestPasswordReset(data: PasswordResetRequest): Promise<{ success: boolean; message?: string; error?: string }> {
    const response = await fetchWithCSRFRetry('/users/password', {
      ...fetchOptions('POST'),
      body: JSON.stringify({
        user: {
          email: data.email,
        },
      }),
    })

    if (!response.ok) {
      return { success: false, error: 'Failed to send reset email' }
    }

    return { success: true, message: 'If your email exists, you will receive reset instructions.' }
  },

  // Reset password with token
  async resetPassword(token: string, password: string, passwordConfirmation: string): Promise<{ success: boolean; error?: string }> {
    const response = await fetchWithCSRFRetry('/users/password', {
      ...fetchOptions('PUT'),
      body: JSON.stringify({
        user: {
          reset_password_token: token,
          password,
          password_confirmation: passwordConfirmation,
        },
      }),
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      return { success: false, error: data.error || 'Failed to reset password' }
    }

    const data = await response.json()
    if (data.csrf_token) {
      updateCSRFToken(data.csrf_token)
    }
    return { success: true }
  },

  // End staff impersonation
  async endImpersonation(): Promise<{ success: boolean }> {
    const response = await fetchWithCSRFRetry('/admin/impersonation', fetchOptions('DELETE'))
    return { success: response.ok }
  },
}
